import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Send, CheckCircle2, Search, PackageCheck, Plus, ClipboardList, Trash2, Pencil, ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { toast } from "sonner";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";

const STATUS_STYLE = {
  draft: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  received: "bg-blue-100 text-blue-800 border-blue-200",
  fulfilled: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-gray-100 text-gray-700 border-gray-200",
};

const TERMS = ["Net 15", "Net 30", "Net 45", "Net 60", "COD", "Prepaid"];
const MODIFICATION_JUSTIFICATIONS = ["Forgot something", "Wrong Item"];
const DELETION_JUSTIFICATIONS = ["Customer Canceled", "Test Order", "Out Of Stock", "Canceled by MPire"];

// ---------- Searchable typeahead combobox (type or click) ----------
function Combobox({ options, value, onChange, placeholder, testId, getLabel = (o) => o?.name, getSubLabel = () => "" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const selected = options.find(o => o.id === value);
    if (selected) setQ(getLabel(selected));
  }, [value, options]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const needle = q.toLowerCase().trim();
  const matches = useMemo(() => {
    if (!needle) return options.slice(0, 12);
    return options.filter(o => {
      const l = (getLabel(o) || "").toLowerCase();
      const s = (getSubLabel(o) || "").toLowerCase();
      return l.includes(needle) || s.includes(needle);
    }).slice(0, 12);
  }, [needle, options]); // eslint-disable-line

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-[var(--border)] rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-menu`}>
          {matches.map(o => (
            <button
              key={o.id}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-soft)] border-b border-[var(--border)] last:border-b-0 ${o.id === value ? "bg-blue-50" : ""}`}
              onClick={() => { onChange(o.id); setQ(getLabel(o)); setOpen(false); }}
              data-testid={`${testId}-opt-${o.id}`}
            >
              <div className="capitalize font-medium">{getLabel(o)}</div>
              {getSubLabel(o) && <div className="text-xs text-gray-500 normal-case">{getSubLabel(o)}</div>}
            </button>
          ))}
        </div>
      )}
      {open && matches.length === 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-[var(--border)] rounded-md shadow-lg p-3 text-xs text-gray-400">No matches</div>
      )}
    </div>
  );
}

const BLANK_PO = { vendor_id: "", client_id: "", items: [{ product_id: "", qty: 1 }], payment_terms: "Net 30", ship_to: "", notes: "" };

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("po-sort") || "date");
  const [sortDirection, setSortDirection] = useState(() => localStorage.getItem("po-sort-direction") || "desc");
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [receiveQty, setReceiveQty] = useState(0);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ payment_terms: "Net 30", ship_to: "", notes: "" });
  const [modificationJustification, setModificationJustification] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteJustification, setDeleteJustification] = useState("");
  const [deleteSaving, setDeleteSaving] = useState(false);

  // Create PO state
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(BLANK_PO);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);

  const load = async () => setPos(await api.get("/purchase-orders").then(r => r.data));

  const loadRelations = async () => {
    const [v, p, c] = await Promise.all([
      api.get("/vendors").then(r => r.data).catch(() => []),
      api.get("/products").then(r => r.data).catch(() => []),
      api.get("/clients?limit=5000").then(r => r.data).catch(() => []),
    ]);
    setVendors(v);
    setProducts(p);
    setClients(Array.isArray(c) ? c : (c.items || c.clients || []));
  };

  useEffect(() => { load(); loadRelations(); }, []);

  const openCreate = () => { setForm(BLANK_PO); setCreateOpen(true); };
  const canManage = (po) => isAdminUser(user) || (user?.role === "personnel" && (po.created_by === user?.id || (po.assigned_to || []).includes(user?.id)));
  const openEdit = (po) => { setEditing(po); setEditForm({ payment_terms: po.payment_terms || "Net 30", ship_to: po.ship_to || "", notes: po.notes || "" }); setModificationJustification(""); setEditOpen(true); };
  const saveEdit = async () => {
    if (!editing) return;
    if (!modificationJustification) { toast.error("Choose a modification justification before saving"); return; }
    setEditSaving(true);
    try {
      await api.patch(`/purchase-orders/${editing.id}`, { ...editForm, justification: modificationJustification });
      toast.success("Purchase order updated and reason recorded"); setEditOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update purchase order"); }
    finally { setEditSaving(false); }
  };
  const openDelete = (po) => { setDeleteTarget(po); setDeleteJustification(""); };
  const deletePO = async () => {
    if (!deleteTarget || !deleteJustification) { toast.error("Choose a deletion justification"); return; }
    setDeleteSaving(true);
    try {
      await api.delete(`/purchase-orders/${deleteTarget.id}`, { data: { justification: deleteJustification } });
      toast.success("Purchase order deleted and reason recorded"); setDeleteTarget(null); setViewOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete purchase order"); }
    finally { setDeleteSaving(false); }
  };

  const submitCreate = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor"); return; }
    const validItems = form.items.filter(i => i.product_id && Number(i.qty) > 0);
    if (validItems.length === 0) { toast.error("Add at least one product with qty > 0"); return; }
    try {
      const { data } = await api.post("/purchase-orders", {
        vendor_id: form.vendor_id,
        client_id: form.client_id || null,
        payment_terms: form.payment_terms,
        ship_to: form.ship_to,
        notes: form.notes,
        items: validItems.map(i => ({ product_id: i.product_id, qty: Number(i.qty) })),
      });
      toast.success(`PO ${data.po_number} created · ${validItems.length} product${validItems.length === 1 ? "" : "s"}`);
      setCreateOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addLine = () => setForm(f => ({ ...f, items: [...f.items, { product_id: "", qty: 1 }] }));
  const updateLine = (idx, patch) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const removeLine = (idx) => setForm(f => ({ ...f, items: f.items.length === 1 ? f.items : f.items.filter((_, i) => i !== idx) }));

  const resend = (po) => {
    const subject = `Resend: Purchase Order ${po.po_number} — ${po.product_name}`;
    const body = [
      `Hello ${po.vendor_poc_name || po.vendor_business_name},`,
      "",
      `Following up on our purchase order:`,
      `PO Number: ${po.po_number}`,
      `Product: ${po.product_name}`,
      `Quantity: ${po.qty}`,
      `Payment terms: ${po.payment_terms}`,
      `Ship to: ${po.ship_to || ""}`,
      "",
      `Please confirm receipt and expected ship date by replying to this email.`,
      "",
      "Thank you,",
      "MPower! Admin",
    ].join("\n");
    const params = new URLSearchParams({ subject, body });
    window.open(`mailto:${encodeURIComponent(po.vendor_poc_email || "")}?${params.toString()}`, "_blank");
  };

  const confirmManual = async (po) => {
    try { await api.post(`/purchase-orders/${po.id}/confirm`); toast.success(`PO ${po.po_number} confirmed`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openReceive = (po) => { setReceiving(po); setReceiveQty(po.qty || 0); setReceiveOpen(true); };
  const submitReceive = async () => {
    try {
      const { data } = await api.post(`/purchase-orders/${receiving.id}/receive`, { received_qty: Number(receiveQty) });
      toast.success(`Received ${receiveQty} × ${receiving.product_name}. Warehouse stock now ${data.product.warehouse_stock}.`);
      setReceiveOpen(false); setReceiving(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => { localStorage.setItem("po-sort", sortBy); localStorage.setItem("po-sort-direction", sortDirection); }, [sortBy, sortDirection]);
  const chooseSort = (field) => {
    if (sortBy === field) setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDirection(field === "date" || field === "qty" ? "desc" : "asc"); }
  };
  const SortHeader = ({ field, children, className = "" }) => {
    const Icon = sortBy === field ? (sortDirection === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return <th className={className}><button type="button" onClick={() => chooseSort(field)} className="inline-flex items-center gap-1 hover:text-[var(--ink)] transition-colors" data-testid={`po-sort-${field}`}>{children}<Icon size={12} /></button></th>;
  };
  const filtered = useMemo(() => {
    const rows = pos.filter(p => !q || [p.po_number, p.vendor_business_name, p.product_name].some(s => s?.toLowerCase().includes(q.toLowerCase())));
    rows.sort((a, b) => {
      let compare = 0;
      if (sortBy === "vendor") compare = (a.vendor_business_name || "").localeCompare(b.vendor_business_name || "");
      else if (sortBy === "qty") compare = Number(a.qty || 0) - Number(b.qty || 0);
      else if (sortBy === "status") compare = (a.status || "").localeCompare(b.status || "");
      else compare = (a.created_at || "").localeCompare(b.created_at || "");
      return sortDirection === "asc" ? compare : -compare;
    });
    return rows;
  }, [pos, q, sortBy, sortDirection]);

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="bg-[var(--brand)] text-white rounded-lg p-2.5 mt-1">
            <ClipboardList size={22} />
          </div>
          <div>
            <p className="label-eyebrow">Vendor commitments</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Purchase Orders</h1>
            <p className="text-gray-500 mt-1 text-sm">Every reorder draft, sent, confirmed, received, or fulfilled — at a glance.</p>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <Input className="pl-8 w-64" placeholder="Search PO # / vendor / product…" value={q} onChange={e => setQ(e.target.value)} data-testid="po-search" />
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-brand" onClick={openCreate} data-testid="create-po-button"><Plus size={14} className="mr-1" /> Create PO</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-po-dialog">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardList size={18} /> New Purchase Order</DialogTitle><DialogDescription>Build a vendor purchase order with products, quantities, and delivery details.</DialogDescription></DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <Label>Vendor *</Label>
                  <Combobox
                    options={vendors}
                    value={form.vendor_id}
                    onChange={(id) => {
                      const v = vendors.find(x => x.id === id);
                      setForm(f => ({ ...f, vendor_id: id, ship_to: f.ship_to || v?.mailing_address || "" }));
                    }}
                    placeholder="Type or pick a vendor…"
                    testId="po-vendor-picker"
                    getLabel={(o) => o?.company || o?.name}
                    getSubLabel={(o) => o?.poc_name ? `POC: ${o.poc_name}${o.poc_email ? " · " + o.poc_email : ""}` : (o?.poc_email || "")}
                  />
                </div>
                <div>
                  <Label>Reserve for client <span className="text-gray-400 text-xs">(optional)</span></Label>
                  <Combobox
                    options={clients}
                    value={form.client_id}
                    onChange={(id) => setForm(f => ({ ...f, client_id: id }))}
                    placeholder="Type or pick a client…"
                    testId="po-client-picker"
                    getLabel={(o) => o?.name || o?.company || "—"}
                    getSubLabel={(o) => o?.company || o?.email || ""}
                  />
                  {form.client_id && (
                    <button type="button" className="text-xs text-[var(--brand)] mt-1 underline" onClick={() => setForm(f => ({ ...f, client_id: "" }))} data-testid="po-client-clear">Clear client</button>
                  )}
                </div>
                {/* Multi-product line items */}
                <div className="border border-[var(--border)] rounded-md p-3 space-y-2" data-testid="po-line-items">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wide text-gray-500">Products *</Label>
                    <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="po-add-line" className="h-7 text-xs">
                      <Plus size={12} className="mr-1" /> Add product
                    </Button>
                  </div>
                  {form.items.map((line, idx) => (
                    <div key={idx} className="flex gap-2 items-start" data-testid={`po-line-${idx}`}>
                      <div className="flex-1 min-w-0">
                        <Combobox
                          options={products}
                          value={line.product_id}
                          onChange={(id) => updateLine(idx, { product_id: id })}
                          placeholder="Product…"
                          testId={`po-line-product-${idx}`}
                          getLabel={(o) => o?.name}
                          getSubLabel={(o) => o?.sku ? `${o.sku} · $${Number(o.wholesale_price || 0).toFixed(2)}` : ""}
                        />
                      </div>
                      <Input
                        type="number"
                        min="1"
                        value={line.qty}
                        onChange={e => updateLine(idx, { qty: e.target.value })}
                        className="w-20 shrink-0"
                        data-testid={`po-line-qty-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLine(idx)}
                        disabled={form.items.length === 1}
                        className="shrink-0 text-gray-400 hover:text-red-600"
                        data-testid={`po-line-remove-${idx}`}
                      ><Trash2 size={14} /></Button>
                    </div>
                  ))}
                </div>
                <div>
                  <Label>Payment terms</Label>
                  <select className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white" value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} data-testid="po-terms">
                    {TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Ship to</Label>
                  <Input value={form.ship_to} onChange={e => setForm({ ...form, ship_to: e.target.value })} placeholder="Auto-fills from vendor mailing address" data-testid="po-shipto" />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="po-notes" />
                </div>
                <Button className="btn-brand w-full" onClick={submitCreate} data-testid="po-submit-create">
                  <Plus size={14} className="mr-1" /> Create purchase order
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[980px] w-full text-sm" data-testid="po-table">
            <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3 w-10"><SelectAllCheckbox records={filtered} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /></th>
                <th className="text-left p-3 min-w-[104px]">PO #</th>
                <SortHeader field="date" className="text-left p-3 min-w-[92px]">Date</SortHeader>
                <SortHeader field="vendor" className="text-left p-3 min-w-[170px]">Vendor</SortHeader>
                <th className="text-left p-3">Product</th>
                <SortHeader field="qty" className="text-right p-3">Qty</SortHeader>
                <th className="text-left p-3">Terms</th>
                <SortHeader field="status" className="text-left p-3">Status</SortHeader>
                <th className="text-left p-3">Source</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="table-row border-t border-[var(--border)] hover:bg-gray-50 cursor-pointer" onClick={() => { setViewing(p); setViewOpen(true); }} data-testid={`po-row-${p.id}`}>
                  <td className="p-3" onClick={(event) => event.stopPropagation()}><SelectionCheckbox checked={selectedIds.includes(p.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, p.id] : selectedIds.filter((id) => id !== p.id))} testId={`select-row-checkbox-${p.id}`} label={`Select ${p.po_number}`} /></td>
                  <td className="p-3 font-mono text-xs text-[var(--brand)] underline whitespace-nowrap">{p.po_number}</td>
                  <td className="p-3 whitespace-nowrap">{p.created_at?.slice(0, 10)}</td>
                  <td className="min-w-[170px] p-3 capitalize break-words">{p.vendor_business_name}<div className="text-xs text-gray-500 normal-case break-all">{p.vendor_poc_email || "—"}</div></td>
                  <td className="p-3 capitalize">
                    {p.product_name}
                    {p.items && p.items.length > 1 && (
                      <span className="ml-1 text-xs text-gray-500">+{p.items.length - 1} more</span>
                    )}
                  </td>
                  <td className="p-3 text-right tabular-nums">{p.received_qty ? <span title={`Received ${p.received_qty}`}>{p.qty} <span className="text-emerald-600 text-xs">✓{p.received_qty}</span></span> : p.qty}</td>
                  <td className="p-3">{p.payment_terms}</td>
                  <td className="p-3"><span className={`status-pill ${STATUS_STYLE[p.status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>{p.status}</span></td>
                  <td className="p-3 text-xs text-gray-500">{p.confirmation_source || "—"}{p.confirmation_from ? ` · ${p.confirmation_from}` : ""}</td>
                  <td className="p-3 text-right space-x-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {isAdminUser(user) && <Button size="sm" variant="outline" onClick={() => resend(p)} data-testid={`po-resend-${p.id}`}><Send size={12} className="mr-1" /> Resend</Button>}
                    {isAdminUser(user) && p.status === "draft" && (
                      <Button size="sm" className="btn-brand" onClick={() => confirmManual(p)} data-testid={`po-confirm-${p.id}`}><CheckCircle2 size={12} className="mr-1" /> Confirm</Button>
                    )}
                    {isAdminUser(user) && p.status === "confirmed" && (
                      <Button size="sm" className="btn-brand" onClick={() => openReceive(p)} data-testid={`po-receive-${p.id}`}><PackageCheck size={12} className="mr-1" /> Receive</Button>
                    )}
                    {canManage(p) && p.status !== "received" && <Button size="icon" variant="ghost" title="Modify purchase order" onClick={() => openEdit(p)} data-testid={`edit-po-${p.id}`}><Pencil size={14} /></Button>}
                    {canManage(p) && <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700" title={p.status === "received" ? "Received purchase orders cannot be deleted" : "Delete purchase order"} disabled={p.status === "received"} onClick={() => openDelete(p)} data-testid={`delete-po-${p.id}`}><Trash2 size={14} /></Button>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-gray-400">No purchase orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <BulkActions section="purchase_orders" records={filtered} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={load} />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-xl max-h-[90dvh] overflow-x-hidden overflow-y-auto p-4 sm:p-6" data-testid="edit-po-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil size={17} /> Modify purchase order</DialogTitle><DialogDescription>Record why this vendor commitment is changing.</DialogDescription></DialogHeader>
          {editing && <div className="space-y-4">
            <div className="text-sm text-gray-600" data-testid="edit-po-summary"><span className="font-mono font-semibold text-gray-900">{editing.po_number}</span> · <span className="capitalize">{editing.vendor_business_name}</span></div>
            <div><Label htmlFor="edit-po-terms">Payment terms</Label><select id="edit-po-terms" className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white mt-1" value={editForm.payment_terms} onChange={(e) => setEditForm({ ...editForm, payment_terms: e.target.value })} data-testid="edit-po-terms-select">{TERMS.map((term) => <option key={term} value={term}>{term}</option>)}</select></div>
            <div><Label htmlFor="edit-po-shipto">Ship to</Label><Input id="edit-po-shipto" value={editForm.ship_to} onChange={(e) => setEditForm({ ...editForm, ship_to: e.target.value })} data-testid="edit-po-shipto-input" /></div>
            <div><Label htmlFor="edit-po-notes">Notes</Label><Input id="edit-po-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-po-notes-input" /></div>
            <div><Label htmlFor="po-modification-justification">Modification justification *</Label><select id="po-modification-justification" className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white mt-1" value={modificationJustification} onChange={(e) => setModificationJustification(e.target.value)} data-testid="po-modification-justification-select"><option value="">Select a reason</option>{MODIFICATION_JUSTIFICATIONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}</select></div>
            <Button className="btn-brand w-full" onClick={saveEdit} disabled={editSaving} data-testid="save-po-edit-button">{editSaving ? "Saving…" : "Save purchase order"}</Button>
          </div>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent data-testid="delete-po-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><Trash2 size={17} /> Delete purchase order</DialogTitle><DialogDescription>This removes the PO from active lists and records the selected reason.</DialogDescription></DialogHeader>
          {deleteTarget && <div className="space-y-4"><div className="text-sm text-gray-600" data-testid="delete-po-summary"><span className="font-mono font-semibold text-gray-900">{deleteTarget.po_number}</span> · <span className="capitalize">{deleteTarget.vendor_business_name}</span></div><div><Label htmlFor="po-delete-justification">Deletion justification *</Label><select id="po-delete-justification" className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white mt-1" value={deleteJustification} onChange={(e) => setDeleteJustification(e.target.value)} data-testid="po-delete-justification-select"><option value="">Select a reason</option>{DELETION_JUSTIFICATIONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}</select></div><Button className="w-full bg-red-600 hover:bg-red-700 text-white" onClick={deletePO} disabled={deleteSaving || !deleteJustification} data-testid="confirm-delete-po-button">{deleteSaving ? "Deleting…" : "Delete purchase order"}</Button></div>}
        </DialogContent>
      </Dialog>

      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Receive PO {receiving?.po_number}</DialogTitle><DialogDescription>Confirm the quantity delivered before warehouse inventory is updated.</DialogDescription></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-gray-600 capitalize">
              <strong>Product:</strong> {receiving?.product_name}<br />
              <span className="normal-case"><strong>Vendor:</strong> {receiving?.vendor_business_name}</span><br />
              <span className="normal-case"><strong>Ordered qty:</strong> {receiving?.qty}</span>
            </p>
            <div>
              <Label>Actually received</Label>
              <Input type="number" min="1" value={receiveQty} onChange={e => setReceiveQty(e.target.value)} data-testid="receive-qty" />
              <p className="text-xs text-gray-500 mt-1">Will increment warehouse stock and log a stock movement.</p>
            </div>
            <Button className="btn-brand w-full" onClick={submitReceive} disabled={!receiveQty || Number(receiveQty) <= 0} data-testid="submit-receive">
              <PackageCheck size={14} className="mr-1" /> Confirm receipt & stock
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View-only PO detail */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-lg" data-testid="po-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-sm">{viewing?.po_number}</span>
              <span className={`status-pill ${STATUS_STYLE[viewing?.status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>{viewing?.status}</span>
            </DialogTitle>
            <DialogDescription>Review the vendor, line items, delivery details, and purchase order activity.</DialogDescription>
          </DialogHeader>
          {viewing && (
            <dl className="text-sm space-y-2 divide-y divide-[var(--border)]" data-testid="po-view-details">
              <Row k="Vendor"><span className="capitalize">{viewing.vendor_business_name || "—"}</span></Row>
              <Row k="Vendor POC">{viewing.vendor_poc_name ? <span className="capitalize">{viewing.vendor_poc_name}</span> : "—"}{viewing.vendor_poc_email && <div className="text-xs text-gray-500">{viewing.vendor_poc_email}</div>}</Row>
              <Row k="Vendor phone">{viewing.vendor_poc_phone || "—"}</Row>
              {viewing.items && viewing.items.length > 0 ? (
                <div className="pt-2">
                  <div className="text-gray-500 label-eyebrow mb-2">Line items · {viewing.items.length}</div>
                  <ul className="border border-[var(--border)] rounded-md divide-y divide-[var(--border)]" data-testid="po-view-line-items">
                    {viewing.items.map((it, i) => (
                      <li key={i} className="p-2 flex justify-between text-sm">
                        <span className="capitalize truncate flex-1">{it.product_name}{it.sku && <span className="text-xs text-gray-500 font-mono ml-2">{it.sku}</span>}</span>
                        <span className="font-semibold tabular-nums ml-3">{it.qty}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <>
                  <Row k="Product"><span className="capitalize">{viewing.product_name}</span></Row>
                  <Row k="Quantity ordered">{viewing.qty}</Row>
                </>
              )}
              {viewing.received_qty > 0 && <Row k="Quantity received"><span className="text-emerald-700 font-semibold">{viewing.received_qty}</span></Row>}
              <Row k="Payment terms">{viewing.payment_terms}</Row>
              <Row k="Ship to">{viewing.ship_to || "—"}</Row>
              <Row k="Created">{viewing.created_at?.slice(0, 19).replace("T", " ")}</Row>
              {viewing.confirmed_at && <Row k="Confirmed">{viewing.confirmed_at?.slice(0, 19).replace("T", " ")}{viewing.confirmation_source ? ` · via ${viewing.confirmation_source}` : ""}</Row>}
              {viewing.received_at && <Row k="Received">{viewing.received_at?.slice(0, 19).replace("T", " ")}</Row>}
              {viewing.notes && <Row k="Notes"><span className="whitespace-pre-wrap">{viewing.notes}</span></Row>}
              {canManage(viewing) && <div className="pt-3 flex gap-2"><Button size="sm" variant="outline" disabled={viewing.status === "received"} onClick={() => openEdit(viewing)} data-testid="view-edit-po-button"><Pencil size={13} className="mr-1" /> Modify</Button><Button size="icon" variant="outline" className="text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50" title="Delete purchase order" disabled={viewing.status === "received"} onClick={() => openDelete(viewing)} data-testid="view-delete-po-button"><Trash2 size={14} /></Button></div>}
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, children }) => (
  <div className="flex justify-between gap-4 pt-2">
    <dt className="text-gray-500 label-eyebrow">{k}</dt>
    <dd className="text-right">{children}</dd>
  </div>
);
